import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ShoppingCart, Package, Ticket, Search, ShoppingBag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useCart, formatPrice } from "@/lib/cart";
import api from "@/lib/api";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const { totalItems } = useCart();

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = category !== "all" ? `?category=${category}` : "";
    api.get(`/shop/products${params}`)
      .then(({ data }) => setProducts(data))
      .catch(() => setError("Unable to load products. Please try again."))
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = search.trim()
    ? products.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.description || "").toLowerCase().includes(search.toLowerCase())
      )
    : products;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1
              className="text-3xl sm:text-4xl font-semibold tracking-tight text-[hsl(var(--fg))]"
              data-testid="shop-page-title"
            >
              Club Shop
            </h1>
            <p className="mt-1 text-sm text-[hsl(var(--muted-fg))]">
              Official Derwent Cricket Club merchandise & event tickets
            </p>
          </div>
          <Link to="/shop/cart" data-testid="shop-cart-link">
            <Button
              variant="outline"
              className="relative gap-2 min-h-[44px] border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"
            >
              <ShoppingCart className="h-4 w-4" />
              Cart
              {totalItems > 0 && (
                <Badge
                  data-testid="shop-cart-badge"
                  className="absolute -top-2 -right-2 h-5 min-w-[20px] px-1.5 text-[10px] font-bold bg-[hsl(var(--accent))] text-[hsl(var(--accent-fg))] border-0"
                >
                  {totalItems > 9 ? "9+" : totalItems}
                </Badge>
              )}
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Tabs value={category} onValueChange={setCategory} className="w-full sm:w-auto">
            <TabsList className="bg-[hsl(var(--secondary))]" data-testid="shop-category-tabs">
              <TabsTrigger value="all" data-testid="shop-tab-all">All</TabsTrigger>
              <TabsTrigger value="merchandise" data-testid="shop-tab-merchandise">
                <Package className="h-3.5 w-3.5 mr-1.5" /> Merchandise
              </TabsTrigger>
              <TabsTrigger value="tickets" data-testid="shop-tab-tickets">
                <Ticket className="h-3.5 w-3.5 mr-1.5" /> Tickets
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-fg))]" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="shop-search-input"
            />
          </div>
        </div>

        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-product-grid-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-[hsl(var(--border))]">
                <Skeleton className="h-48 w-full" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-9 w-full mt-3" />
                </div>
              </Card>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16" data-testid="shop-error-state">
            <ShoppingBag className="h-12 w-12 mx-auto text-[hsl(var(--muted-fg))] mb-4" />
            <p className="text-[hsl(var(--muted-fg))]">{error}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => window.location.reload()}
              data-testid="shop-retry-button"
            >
              Try again
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="shop-empty-state">
            <ShoppingBag className="h-12 w-12 mx-auto text-[hsl(var(--muted-fg))] mb-4" />
            <p className="text-lg font-medium text-[hsl(var(--fg))]">No products found</p>
            <p className="text-sm text-[hsl(var(--muted-fg))] mt-1">
              {search ? "Try a different search term" : "Check back soon for new items!"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-product-grid">
            {filtered.map((product) => (
              <ProductCard key={product.id || product.square_id} product={product} />
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}

function ProductCard({ product }) {
  const minPrice = Math.min(...(product.variations || []).map((v) => v.price_cents || 0));
  const maxPrice = Math.max(...(product.variations || []).map((v) => v.price_cents || 0));
  const hasImage = product.image_urls && product.image_urls.length > 0;
  const currency = (product.variations || [])[0]?.currency || "AUD";

  return (
    <Link to={`/shop/${product.id || product.square_id}`} data-testid={`shop-product-card-${product.id || product.square_id}`}>
      <Card className="overflow-hidden border border-[hsl(var(--border))] shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] hover:-translate-y-[1px] transition-shadow duration-200 cursor-pointer h-full flex flex-col">
        <div className="relative h-48 bg-[hsl(var(--secondary))] overflow-hidden">
          {hasImage ? (
            <img
              src={product.image_urls[0]}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              {product.category === "tickets" ? (
                <Ticket className="h-12 w-12 text-[hsl(var(--muted-fg))]" />
              ) : (
                <Package className="h-12 w-12 text-[hsl(var(--muted-fg))]" />
              )}
            </div>
          )}
          <Badge
            className="absolute top-3 left-3 text-[10px] font-semibold"
            variant={product.category === "tickets" ? "default" : "secondary"}
          >
            {product.category === "tickets" ? "Ticket" : "Merch"}
          </Badge>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="font-semibold text-[hsl(var(--fg))] line-clamp-2">{product.name}</h3>
          {product.description && (
            <p className="text-xs text-[hsl(var(--muted-fg))] mt-1 line-clamp-2">{product.description}</p>
          )}
          <div className="mt-auto pt-3">
            <span className="text-lg font-bold text-[hsl(var(--fg))]">
              {minPrice === maxPrice
                ? formatPrice(minPrice, currency)
                : `${formatPrice(minPrice, currency)} – ${formatPrice(maxPrice, currency)}`}
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
